/**
 * SessionPerformanceHero — dark KPI hero for the Session Performance page.
 *
 * Matches HomeLastSessionHero: Mixed | Levels toggle when both ladders exist in
 * session or snapshot recap; ladder-specific KPI tiles for the active view.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { format, parseISO } from 'date-fns';
import RankMovementLabel from '../leaderboard/RankMovementLabel';
import { homeDarkHeroShell } from '../home/homeDashboardStyles';
import {
  StatTile,
  KpiWithChange,
  WinRateDonut,
  HeroEloDelta,
  heroKpiClass,
  kpiHideBelowMd,
  KpiSkeletonGrid,
  PegBoardSessionTiles,
} from './SessionKpiStrip';
import { SessionLadderToggle } from './SessionLadderToggle';

// ─── Performance tone copy ─────────────────────────────────────────────────────

function getPerformanceTone(winRatePct, totalGames) {
  if (totalGames === 0) return null;
  if (winRatePct === 100) return '🏆 Perfect session — unbeaten!';
  if (winRatePct >= 75) return '🔥 Outstanding session';
  if (winRatePct >= 50) return '💪 Solid session';
  if (winRatePct >= 25) return 'Tough session — keep grinding';
  return 'Hard session — every loss is a lesson';
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function ladderViewFromKey(key) {
  return key === 'mixed' ? 'mixed' : 'levels';
}

function resolveActiveLadder(ladders, ladderView) {
  if (!ladders) return null;
  if (ladderView === 'levels' && ladders.levels) return ladders.levels;
  return ladders.mixed ?? ladders.levels ?? null;
}

// ─── Ladder KPI row (matches HomeLastSessionHero) ──────────────────────────────

function LadderModeTiles({ ladder }) {
  if (!ladder) return null;

  const rankKpi = ladder.rank != null ? `#${ladder.rank}` : '—';
  const eloKpi = ladder.eloRating != null ? ladder.eloRating.toLocaleString() : '—';
  const winRatePct = ladder.winRatePct ?? null;
  const eloDelta = ladder.eloDelta ?? 0;
  const ladderGamesPlayed = ladder.gamesPlayed ?? 0;
  const maxWinStreak = ladder.maxWinStreak ?? 0;
  const ladderLabel = ladder.label ?? 'Mixed';

  return (
    <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2 sm:gap-3 items-stretch">
      <StatTile
        label={`Rank (${ladderLabel})`}
        kpi={
          <KpiWithChange
            value={rankKpi}
            change={<RankMovementLabel rankDelta={ladder.rankDelta} size="sm" />}
          />
        }
      />

      <StatTile
        label={`Elo Rating (${ladderLabel})`}
        kpi={
          <KpiWithChange
            value={eloKpi}
            change={<HeroEloDelta delta={eloDelta} />}
          />
        }
      />

      <StatTile
        className={kpiHideBelowMd}
        label={`Games Played (${ladderLabel})`}
        kpi={
          <span className={heroKpiClass}>
            {ladderGamesPlayed > 0 ? ladderGamesPlayed : '—'}
          </span>
        }
      />

      <StatTile
        label={`Win Rate (${ladderLabel})`}
        kpi={
          <div className="flex items-center gap-1.5 sm:gap-2 min-w-0">
            <div className="hidden sm:block shrink-0">
              <WinRateDonut pct={winRatePct} size={32} />
            </div>
            <span className={`${heroKpiClass} shrink-0`}>
              {winRatePct != null ? `${winRatePct}%` : '—'}
            </span>
          </div>
        }
      />

      <StatTile
        className={kpiHideBelowMd}
        label={`Win Streak (${ladderLabel})`}
        kpi={
          <span className={`${heroKpiClass} flex items-center gap-1.5`}>
            {maxWinStreak >= 3 && <span className="text-lg sm:text-xl" aria-hidden>🔥</span>}
            {maxWinStreak}
          </span>
        }
      />
    </div>
  );
}

/**
 * Props:
 *  sessionInfo         — { name, session_date, clubs: { name } }
 *  ladders             — result of buildSessionLadderSnapshot: { mixed, levels, defaultLadderKey, canToggleLadder }
 *  sessionStats        — { wins, losses, winRatePct, maxWinStreak, mixedEloDelta, levelsEloDelta }
 *  loading             — true while game data is still loading
 *  ladderView          — optional controlled 'mixed'|'levels' value; when provided, the hero and
 *                        the caller (e.g. the Insights tab) share the same toggle state
 *  onLadderViewChange  — required when ladderView is controlled
 *  variant            — 'competitive' (default) or 'pegBoard' (no rank/Elo/ladder)
 */
export function SessionPerformanceHero({
  sessionInfo,
  ladders,
  sessionStats,
  loading,
  ladderView: controlledLadderView,
  onLadderViewChange,
  variant = 'competitive',
}) {
  const defaultView = useMemo(
    () => ladderViewFromKey(ladders?.defaultLadderKey ?? 'mixed'),
    [ladders?.defaultLadderKey]
  );

  const [internalLadderView, setInternalLadderView] = useState(defaultView);
  const isControlled = controlledLadderView !== undefined;
  const ladderView = isControlled ? controlledLadderView : internalLadderView;
  const setLadderView = isControlled ? onLadderViewChange : setInternalLadderView;

  useEffect(() => {
    if (!isControlled) {
      setInternalLadderView(defaultView);
    } else {
      onLadderViewChange?.(defaultView);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only reset on ladder default / session change
  }, [defaultView, sessionInfo?.id]);

  const activeLadder = useMemo(
    () => resolveActiveLadder(ladders, ladderView),
    [ladders, ladderView]
  );

  const isPegBoard = variant === 'pegBoard';
  const canToggleLadder = !isPegBoard && Boolean(ladders?.canToggleLadder);
  const mixedToggleLabel = ladders?.mixed?.toggleLabel ?? 'Mixed';
  const levelsToggleLabel = ladders?.levels?.toggleLabel ?? 'Levels';

  const sessionDateLabel = sessionInfo?.session_date
    ? format(parseISO(sessionInfo.session_date), 'EEEE, d MMMM yyyy')
    : null;

  const totalGames = (sessionStats?.wins ?? 0) + (sessionStats?.losses ?? 0);
  const performanceTone = sessionStats
    ? getPerformanceTone(sessionStats.winRatePct, totalGames)
    : null;

  return (
    <div className={`${homeDarkHeroShell} mb-6`}>
      <div className="p-5 sm:p-7">
        <div className="mb-4">
          <p className="text-xs font-semibold uppercase tracking-widest text-white/40 mb-1.5">
            {isPegBoard ? 'Session recap' : 'Session Performance'}
          </p>
          <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-2">
            <div className="min-w-0 flex-1">
              <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-white leading-snug line-clamp-2">
                {sessionInfo?.name ?? 'Session'}
              </h1>
              <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-sm text-white/50">
                {sessionInfo?.clubs?.name ? (
                  <span>{sessionInfo.clubs.name}</span>
                ) : null}
                {sessionInfo?.clubs?.name && sessionDateLabel ? (
                  <span className="text-white/25" aria-hidden>&middot;</span>
                ) : null}
                {sessionDateLabel ? <span>{sessionDateLabel}</span> : null}
              </div>
              {performanceTone && !loading ? (
                <p className="mt-2.5 text-sm font-medium text-white/70">
                  {performanceTone}
                </p>
              ) : null}
            </div>

            {!loading && canToggleLadder ? (
              <SessionLadderToggle
                tabs={[
                  { id: 'mixed', label: mixedToggleLabel },
                  { id: 'levels', label: levelsToggleLabel },
                ]}
                value={ladderView}
                onChange={setLadderView}
              />
            ) : null}
          </div>
        </div>

        {loading ? (
          <KpiSkeletonGrid hiddenIndexes={isPegBoard ? [] : [2, 4]} />
        ) : isPegBoard ? (
          <PegBoardSessionTiles sessionStats={sessionStats} />
        ) : (
          <LadderModeTiles ladder={activeLadder} />
        )}
      </div>
    </div>
  );
}
